import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Header } from '../components';
import { FiUserPlus, FiRefreshCw, FiSearch, FiEye, FiEdit, FiTrash2, FiUsers, 
         FiMessageCircle, FiBookOpen, FiSave, FiX, FiLoader } from 'react-icons/fi';
         import { FaWhatsapp } from 'react-icons/fa'; // Font Awesome

const Student = () => {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50;
  
  // Form and modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentStudent, setCurrentStudent] = useState(null);
  
  // Individual search filters
  const [filters, setFilters] = useState({
    s_id: '',
    f_name: '',
    phonenumber: '',
    gender: '',
    c_entry: '',
    c_school: '',
    state: '',
    dob: '',
    created_at: ''
  });

  // Sort configuration
  const [sortConfig, setSortConfig] = useState({
    key: 'created_at',
    direction: 'desc'
  });

  useEffect(() => {
    fetchStudents();
  }, []);

  const fetchStudents = async () => {
    try {
      setLoading(true);
      const response = await axios.get('https://apissc.sainikschoolcadet.com/api/Students/showStudents');
      
      // Sort by created_at in descending order by default
      const sortedData = [...response.data].sort((a, b) => {
        const dateA = new Date(a.created_at || 0);
        const dateB = new Date(b.created_at || 0);
        return dateB - dateA;
      });
      
      setStudents(sortedData);
      setLoading(false);
    } catch (err) {
      setError('Failed to fetch student data');
      setLoading(false);
      console.error('Error fetching students:', err);
    }
  };

  const handleCreateClick = () => {
    setCurrentStudent(null);
    setIsEditing(false);
    setIsModalOpen(true);
  };

  const handleEditClick = (student) => {
    setCurrentStudent(student);
    setIsEditing(true);
    setIsModalOpen(true);
  };

  const handleModalClose = (refreshData = false) => {
    setIsModalOpen(false);
    if (refreshData) {
      fetchStudents();
    }
  };

  const formatDate = (date) => {
    if (!date) return '';
    const formattedDate = new Date(date).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
    return formattedDate;
  };
  const formatregDate = (date) => {
    if (!date) return '';
    const formattedDateTime = new Date(date).toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  
    return formattedDateTime;
  };
  
  
  const formatTime = (datetime) => {
    if (!datetime) return '';
    const date = new Date(datetime);
    return date.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Handle changes to individual filters
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
    setCurrentPage(1); // Reset to first page when filter changes
  };

  // Filter students based on all individual filters
  const filteredStudents = students.filter(student => {
    return (
      (filters.s_id === '' || (student.s_id && student.s_id.toString().includes(filters.s_id))) &&
      (filters.f_name === '' || (student.f_name && student.f_name.toLowerCase().includes(filters.f_name.toLowerCase()))) &&
      (filters.phonenumber === '' || (student.phonenumber && student.phonenumber.includes(filters.phonenumber))) &&
      (filters.gender === '' || (student.gender && student.gender === filters.gender)) &&
      (filters.c_entry === '' || (student.c_entry && student.c_entry.toLowerCase().includes(filters.c_entry.toLowerCase()))) &&
      (filters.c_school === '' || (student.c_school && student.c_school.toLowerCase().includes(filters.c_school.toLowerCase()))) &&
      (filters.state === '' || (student.state && student.state.toLowerCase().includes(filters.state.toLowerCase()))) &&
      (filters.dob === '' || (student.dob && formatDate(student.dob).includes(filters.dob))) &&
      (filters.created_at === '' || (student.created_at && formatregDate(student.created_at).includes(filters.created_at)))
    );
  });

  // Handle column sorting
  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Sort students based on current sort configuration
  const sortedStudents = React.useMemo(() => {
    const sortableStudents = [...filteredStudents];
    if (sortConfig.key) {
      sortableStudents.sort((a, b) => {
        // Handle date fields specially
        if (sortConfig.key === 'dob' || sortConfig.key === 'created_at') {
          const dateA = new Date(a[sortConfig.key] || 0);
          const dateB = new Date(b[sortConfig.key] || 0);
          
          if (sortConfig.direction === 'asc') {
            return dateA - dateB;
          } else {
            return dateB - dateA;
          }
        }
        
        // Handle regular string fields
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableStudents;
  }, [filteredStudents, sortConfig]);

  // Calculate the statistics
  const statsData = {
    total: filteredStudents.length,
    class6th: filteredStudents.filter(s => s.c_entry === 'Class 6th').length,
    class9th: filteredStudents.filter(s => s.c_entry === 'Class 9th').length
  };

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentStudents = sortedStudents.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(sortedStudents.length / itemsPerPage);
  
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Open WhatsApp chat
  const openWhatsAppChat = (phoneNumber) => {
    if (!phoneNumber) return;
    const formattedNumber = phoneNumber.replace(/\D/g, '');
    window.open(`https://wa.me/+91${formattedNumber}`, '_blank');
  };

  // Get unique values for dropdowns
  const uniqueGenders = [...new Set(students.filter(s => s.gender).map(s => s.gender))];
  const uniqueClassEntries = [...new Set(students.filter(s => s.c_entry).map(s => s.c_entry))];

  // Loading state
  if (loading) {
    return (
      <div className="m-2 md:m-10 mt-24 p-2 md:p-10 bg-white rounded-3xl">
        <Header title="Students" />
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="m-2 md:m-10 mt-24 p-2 md:p-10 bg-white rounded-3xl">
        <Header title="Students" />
        <div className="flex flex-col justify-center items-center h-64">
          <div className="text-red-500 text-xl mb-4">{error}</div>
          <button 
            onClick={fetchStudents}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
          >
            <FiRefreshCw className="mr-2" />
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="m-2 md:m-10 mt-24 p-2 md:p-10 bg-white rounded-3xl shadow-sm">
      {/* Header with title and action buttons */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
        <Header title="Students" />
        <div className="flex space-x-2 mt-4 md:mt-0">
          <button 
            onClick={handleCreateClick}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
          >
            <FiUserPlus className="mr-2" />
            Add Student
          </button>
          <button 
            onClick={fetchStudents}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 flex items-center"
          >
            <FiRefreshCw className="mr-2" />
            Refresh
          </button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-blue-50 p-4 rounded-lg shadow-sm">
          <div className="flex items-center">
            <div className="bg-blue-100 p-3 rounded-full mr-4">
              <FiUsers className="text-blue-700" size={20} />
            </div>
            <div>
              <p className="text-gray-500 text-sm">Total Students</p>
              <p className="text-2xl font-semibold">{statsData.total}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-green-50 p-4 rounded-lg shadow-sm">
          <div className="flex items-center">
            <div className="bg-green-100 p-3 rounded-full mr-4">
              <FiBookOpen className="text-green-700" size={20} />
            </div>
            <div>
              <p className="text-gray-500 text-sm">Class 6th</p>
              <p className="text-2xl font-semibold">{statsData.class6th}</p>
              <p className="text-xs text-green-600">
                {statsData.total ? Math.round((statsData.class6th / statsData.total) * 100) : 0}% of total
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-purple-50 p-4 rounded-lg shadow-sm">
          <div className="flex items-center">
            <div className="bg-purple-100 p-3 rounded-full mr-4">
              <FiBookOpen className="text-purple-700" size={20} />
            </div>
            <div>
              <p className="text-gray-500 text-sm">Class 9th</p>
              <p className="text-2xl font-semibold">{statsData.class9th}</p>
              <p className="text-xs text-purple-600">
                {statsData.total ? Math.round((statsData.class9th / statsData.total) * 100) : 0}% of total
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Student Table */}
      <div className="overflow-x-auto border rounded-lg">
        <table className="min-w-full divide-y divide-gray-200 table-fixed">
          <thead className="bg-gray-50">
            <tr>
              <th className="w-[5%] px-3 py-3 text-center text-sm font-medium text-gray-500 uppercase">
                S No
                <button onClick={() => requestSort('s_id')} className="ml-1 focus:outline-none">
                  {sortConfig.key === 's_id' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : '⇅'}
                </button>
              </th>
              <th className="w-[15%] px-3 py-3 text-center text-sm font-medium text-gray-500 uppercase">
                Name
                <button onClick={() => requestSort('f_name')} className="ml-1 focus:outline-none">
                  {sortConfig.key === 'f_name' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : '⇅'}
                </button>
              </th>
              <th className="w-[10%] px-3 py-3 text-center text-sm font-medium text-gray-500 uppercase">
                Phone Number
                <button onClick={() => requestSort('phonenumber')} className="ml-1 focus:outline-none">
                  {sortConfig.key === 'phonenumber' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : '⇅'}
                </button>
              </th>
              <th className="w-[8%] px-3 py-3 text-center text-sm font-medium text-gray-500 uppercase">
                Gender
                <button onClick={() => requestSort('gender')} className="ml-1 focus:outline-none">
                  {sortConfig.key === 'gender' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : '⇅'}
                </button>
              </th>
              <th className="w-[10%] px-3 py-3 text-center text-sm font-medium text-gray-500 uppercase">
                Class Entry
                <button onClick={() => requestSort('c_entry')} className="ml-1 focus:outline-none">
                  {sortConfig.key === 'c_entry' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : '⇅'}
                </button>
              </th>
              <th className="w-[20%] px-3 py-3 text-center text-sm font-medium text-gray-500 uppercase">
                School Choice
                <button onClick={() => requestSort('c_school')} className="ml-1 focus:outline-none">
                  {sortConfig.key === 'c_school' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : '⇅'}
                </button>
              </th>
              <th className="w-[10%] px-3 py-3 text-center text-sm font-medium text-gray-500 uppercase">
                State
                <button onClick={() => requestSort('state')} className="ml-1 focus:outline-none">
                  {sortConfig.key === 'state' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : '⇅'}
                </button>
              </th>
              <th className="w-[8%] px-3 py-3 text-center text-sm font-medium text-gray-500 uppercase">
                DOB
                <button onClick={() => requestSort('dob')} className="ml-1 focus:outline-none">
                  {sortConfig.key === 'dob' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : '⇅'}
                </button>
              </th>
              <th className="w-[8%] px-3 py-3 text-center text-sm font-medium text-gray-500 uppercase">
                Registration Date
              </th>
              <th className="w-[8%] px-3 py-3 text-center text-sm font-medium text-gray-500 uppercase">
                Actions
              </th>
            </tr>
            <tr>
              {/* Filter Inputs */}
              <th className="px-2 py-2 border-t"></th>
              <th className="px-2 py-2 border-t">
                <input
                  type="text"
                  placeholder="Search Name"
                  className="w-full p-1 text-sm border rounded"
                  value={filters.f_name}
                  onChange={(e) => handleFilterChange('f_name', e.target.value)}
                />
              </th>
              <th className="px-2 py-2 border-t">
                <input
                  type="text"
                  placeholder="Search Phone"
                  className="w-full p-1 text-sm border rounded"
                  value={filters.phonenumber}
                  onChange={(e) => handleFilterChange('phonenumber', e.target.value)}
                />
              </th>
              <th className="px-2 py-2 border-t">
                <select
                  className="w-full p-1 text-sm border rounded"
                  value={filters.gender}
                  onChange={(e) => handleFilterChange('gender', e.target.value)}
                >
                  <option value="">All</option>
                  {uniqueGenders.map(gender => (
                    <option key={gender} value={gender}>{gender}</option>
                  ))}
                </select>
              </th>
              <th className="px-2 py-2 border-t">
                <select
                  className="w-full p-1 text-sm border rounded"
                  value={filters.c_entry}
                  onChange={(e) => handleFilterChange('c_entry', e.target.value)}
                >
                  <option value="">All</option>
                  {uniqueClassEntries.map(entry => (
                    <option key={entry} value={entry}>{entry}</option>
                  ))}
                </select>
              </th>
              <th className="px-2 py-2 border-t">
                <input
                  type="text"
                  placeholder="Search School"
                  className="w-full p-1 text-sm border rounded"
                  value={filters.c_school}
                  onChange={(e) => handleFilterChange('c_school', e.target.value)}
                />
              </th>
              <th className="px-2 py-2 border-t">
                <input
                  type="text"
                  placeholder="Search State"
                  className="w-full p-1 text-sm border rounded"
                  value={filters.state}
                  onChange={(e) => handleFilterChange('state', e.target.value)}
                />
              </th>
              <th className="px-2 py-2 border-t"></th>
              <th className="px-2 py-2 border-t"></th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {currentStudents.map((student, index)  => (
              <tr key={student.s_id} className="hover:bg-gray-50">
                <td className="px-3 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
  {index + 1}
</td>
                <td className="px-3 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="h-8 w-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center mr-3">
                      {student.f_name?.charAt(0)?.toUpperCase() || 'S'}
                    </div>
                    <div className="text-sm font-medium text-gray-900">{student.f_name}</div>
                  </div>
                </td>
                <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">{student.phonenumber}</td>
                <td className="px-3 py-4 whitespace-nowrap">
                  <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${student.gender === 'Male' ? 'bg-blue-100 text-blue-800' : 'bg-pink-100 text-pink-800'}`}>
                    {student.gender}
                  </span>
                </td>
                <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">{student.c_entry}</td>
                <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">{student.c_school}</td>
                <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">{student.state}</td>
                <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">{formatDate(student.dob)}</td>
                <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">{formatregDate(student.created_at)}</td>
                <td className="px-3 py-4 whitespace-nowrap text-center text-sm font-medium">
                  <div className="flex justify-center space-x-4">
                  <button 
  className="text-green-600 hover:text-green-900" 
  title="WhatsApp Chat"
  onClick={() => openWhatsAppChat(student.phonenumber)}
>
  <FaWhatsapp size={18} />
</button>
                    <button 
                      className="text-blue-600 hover:text-blue-900" 
                      title="Edit"
                      onClick={() => handleEditClick(student)}
                    >
                      <FiEdit size={18} />
                    </button>
                    <a 
      href={`/view-student/${student.s_id}`}
      className="text-purple-600 hover:text-purple-900 mr-2" 
      title="View Details"
      target="_blank"
      rel="noopener noreferrer"
    >
      <FiEye size={18} />
    </a>
                  </div>
                </td>
              </tr>
            ))}
            {currentStudents.length === 0 && (
              <tr>
                <td colSpan="9" className="px-6 py-4 text-center text-gray-500">
                  {filteredStudents.length === 0 ? 'No students found' : 'No students match your search criteria'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {filteredStudents.length > itemsPerPage && (
        <div className="flex items-center justify-between mt-4 px-4 py-3 bg-white border-t border-gray-200 sm:px-6">
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Showing <span className="font-medium">{indexOfFirstItem + 1}</span> to{' '}
                <span className="font-medium">
                  {indexOfLastItem > filteredStudents.length ? filteredStudents.length : indexOfLastItem}
                </span>{' '}
                of <span className="font-medium">{filteredStudents.length}</span> results
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                <button
                  onClick={() => paginate(currentPage > 1 ? currentPage - 1 : 1)}
                  disabled={currentPage === 1}
                  className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium ${
                    currentPage === 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'
                  }`}
                >
                  <span className="sr-only">Previous</span>
                  <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </button>
                
                {[...Array(Math.min(5, totalPages))].map((_, index) => {
                  // Show up to 5 pages, centered around current page
                  let pageNum = currentPage;
                  if (currentPage < 3) {
                    pageNum = index + 1;
                  } else if (currentPage > totalPages - 2) {
                    pageNum = totalPages - 4 + index;
                  } else {
                    pageNum = currentPage - 2 + index;
                  }
                  
                  if (pageNum > 0 && pageNum <= totalPages) {
                    return (
                      <button
                        key={pageNum}
                        onClick={() => paginate(pageNum)}
                        className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium ${
                          currentPage === pageNum
                            ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                            : 'bg-white text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  }
                  return null;
                })}
                
                <button
                  onClick={() => paginate(currentPage < totalPages ? currentPage + 1 : totalPages)}
                  disabled={currentPage === totalPages}
                  className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium ${
                    currentPage === totalPages ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'
                  }`}
                >
                  <span className="sr-only">Next</span>
                  <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                  </svg>
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* Student Form Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 overflow-auto bg-black bg-opacity-50 flex justify-center items-center p-4">
          <div className="bg-white rounded-lg shadow-lg overflow-hidden w-full max-w-4xl">
            <div className="bg-blue-600 px-6 py-4">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold text-white">
                  {isEditing ? 'Edit Student' : 'Register New Student'}
                </h2>
                <button 
                  onClick={() => setIsModalOpen(false)} 
                  className="text-white hover:text-gray-200"
                >
                  <FiX size={24} />
                </button>
              </div>
            </div>
            
            <StudentForm 
              isEditing={isEditing} 
              studentData={currentStudent} 
              onClose={handleModalClose} 
            />
          </div>
        </div>
      )}
    </div>
  );
};

// Student Form Component
const StudentForm = ({ isEditing = false, studentData = null, onClose }) => {
  const initialFormState = {
    f_name: '',
    email: '',
    phonenumber: '',
    password: '',
    gender: '',
    dob: '',
    state: '',
    city: '',
    c_entry: '',
    c_school: ''
  };

  const [formData, setFormData] = useState(initialFormState);
  const [newPhonenumber, setNewPhonenumber] = useState('');
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const baseUrl = 'https://apissc.sainikschoolcadet.com/api/Students';
  
  // List of states and schools (predefined for simplicity)
  const states = [
    'Andaman and Nicobar Islands',
    'Andhra Pradesh',
    'Arunachal Pradesh',
    'Assam',
    'Bihar',
    'Chandigarh',
    'Chhattisgarh',
    'Dadra and Nagar Haveli and Daman and Diu',
    'Delhi',
    'Goa',
    'Gujarat',
    'Haryana',
    'Himachal Pradesh',
    'Jammu and Kashmir',
    'Jharkhand',
    'Karnataka',
    'Kerala',
    'Ladakh',
    'Lakshadweep',
    'Madhya Pradesh',
    'Maharashtra',
    'Manipur',
    'Meghalaya',
    'Mizoram',
    'Nagaland',
    'Odisha',
    'Puducherry',
    'Punjab',
    'Rajasthan',
    'Sikkim',
    'Tamil Nadu',
    'Telangana',
    'Tripura',
    'Uttar Pradesh',
    'Uttarakhand',
    'West Bengal'
];
  const schools =[
    'Ambikapur, Chhattisgarh',
    'Amaravathinagar, Tamil Nadu',
    'Amethi, Uttar Pradesh',
    'Balachadi, Gujarat',
    'Bijapur, Karnataka',
    'Bhubaneswar, Odisha',
    'Chandrapur, Maharashtra',
    'Chhingchhip, Mizoram',
    'Chittorgarh, Rajasthan',
    'East Siang, Arunachal Pradesh',
    'Ghorakhal, Uttarakhand',
    'Goalpara, Assam',
    'Gopalganj, Bihar',
    'Imphal, Manipur',
    'Jhansi, Uttar Pradesh',
    'Jhunjhunu, Rajasthan',
    'Kalikiri, Andhra Pradesh',
    'Kapurthala, Punjab',
    'Kazhakootam, Kerala',
    'Kodagu, Karnataka',
    'Korukonda, Andhra Pradesh',
    'Kunjpura, Haryana',
    'Mainpuri, Uttar Pradesh',
    'Nagrota, Jammu and Kashmir',
    'Nalanda, Bihar',
    'Punglwa, Nagaland',
    'Purulia, West Bengal',
    'Rewa, Madhya Pradesh',
    'Rewari, Haryana',
    'Sambalpur, Odisha',
    'Satara, Maharashtra',
    'Sujanpur Tira, Himachal Pradesh',
    'Tilaiya, Jharkhand',
    'UPSS Lucknow, Uttar Pradesh',
    'UPSS Gorakhpur, Uttar Pradesh'
    // More schools can be added
  ]
  // Initialize data if editing
  useEffect(() => {
    if (isEditing && studentData) {
      // Format date from API to yyyy-MM-dd for input type="date"
      const formattedDob = studentData.dob ? new Date(studentData.dob).toISOString().split('T')[0] : '';
      
      setFormData({
        ...studentData,
        dob: formattedDob,
        password: '' // Clear password when editing
      });
      
      setNewPhonenumber(studentData.phonenumber || '');
    }
  }, [isEditing, studentData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Clear error when field is changed
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };

  const validate = () => {
    let tempErrors = {};
    
    if (!formData.f_name) tempErrors.f_name = "Name is required";
    if (!formData.phonenumber) tempErrors.phonenumber = "Phone number is required";
    if (!formData.gender) tempErrors.gender = "Gender is required";
    if (!formData.dob) tempErrors.dob = "Date of birth is required";
    if (!formData.state) tempErrors.state = "State is required";
    if (!formData.c_entry) tempErrors.c_entry = "Class entry is required";
    if (!formData.c_school) tempErrors.c_school = "School choice is required";
    
    // Email validation if provided
    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      tempErrors.email = "Invalid email format";
    }
    
    // Phone number validation
    if (formData.phonenumber && !/^\d{10}$/.test(formData.phonenumber)) {
      tempErrors.phonenumber = "Phone number must be 10 digits";
    }
    
    // Password validation for new students
    if (!isEditing && !formData.password) {
      tempErrors.password = "Password is required for new registration";
    }
    
    // For editing: validate new phone number if changed
    if (isEditing && newPhonenumber !== formData.phonenumber) {
      if (!/^\d{10}$/.test(newPhonenumber)) {
        tempErrors.newPhonenumber = "New phone number must be 10 digits";
      }
    }
    
    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Reset messages
    setSuccessMessage('');
    setErrorMessage('');
    
    if (!validate()) return;
    
    setIsSubmitting(true);
    
    try {
      let response;
      
      if (isEditing) {
        // Update existing student
        response = await axios.put(`${baseUrl}/update`, {
          phonenumber: formData.phonenumber,
          newPhonenumber: newPhonenumber !== formData.phonenumber ? newPhonenumber : undefined,
          f_name: formData.f_name,
          email: formData.email,
          gender: formData.gender,
          dob: formData.dob,
          state: formData.state,
          city: formData.city,
          c_entry: formData.c_entry,
          c_school: formData.c_school,
          password: formData.password || undefined
        });
        
        setSuccessMessage('Student updated successfully!');
      } else {
        // Register new student
        response = await axios.post(`${baseUrl}/register`, {
          f_name: formData.f_name,
          email: formData.email,
          phonenumber: formData.phonenumber,
          password: formData.password,
          gender: formData.gender,
          dob: formData.dob,
          state: formData.state,
          city: formData.city,
          c_entry: formData.c_entry,
          c_school: formData.c_school
        });
        
        setSuccessMessage('Student registered successfully!');
        setFormData(initialFormState);
      }
      
      // Close modal after a delay
      setTimeout(() => {
        if (onClose) {
          onClose(true); // Indicate success to the parent component
        }
      }, 2000);
      
    } catch (error) {
      console.error('Error submitting form:', error);
      setErrorMessage(error.response?.data?.error || 'An error occurred while processing your request');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="p-6">
      {/* Success/Error Messages */}
      {successMessage && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
          {successMessage}
        </div>
      )}
      
      {errorMessage && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {errorMessage}
        </div>
      )}
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Full Name */}
        <div>
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="f_name">
            Full Name *
          </label>
          <input
            id="f_name"
            name="f_name"
            type="text"
            value={formData.f_name}
            onChange={handleChange}
            className={`shadow appearance-none border ${errors.f_name ? 'border-red-500' : 'border-gray-300'} rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500`}
            placeholder="Enter full name"
          />
          {errors.f_name && <p className="text-red-500 text-xs mt-1">{errors.f_name}</p>}
        </div>
        
        {/* Email */}
        <div>
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="email">
            Email
          </label>
          <input
            id="email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            className={`shadow appearance-none border ${errors.email ? 'border-red-500' : 'border-gray-300'} rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500`}
            placeholder="Enter email address"
          />
          {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
        </div>
        
        {/* Phone Number */}
        <div>
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="phonenumber">
            Phone Number *
          </label>
          <input
            id="phonenumber"
            name="phonenumber"
            type="text"
            value={formData.phonenumber}
            onChange={handleChange}
            className={`shadow appearance-none border ${errors.phonenumber ? 'border-red-500' : 'border-gray-300'} rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500`}
            placeholder="10-digit mobile number"
            maxLength={10}
            disabled={isEditing}
          />
          {errors.phonenumber && <p className="text-red-500 text-xs mt-1">{errors.phonenumber}</p>}
        </div>
        
        {/* New Phone Number (Only for editing) */}
        {isEditing && (
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="newPhonenumber">
              New Phone Number
            </label>
            <input
              id="newPhonenumber"
              name="newPhonenumber"
              type="text"
              value={newPhonenumber}
              onChange={(e) => setNewPhonenumber(e.target.value)}
              className={`shadow appearance-none border ${errors.newPhonenumber ? 'border-red-500' : 'border-gray-300'} rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500`}
              placeholder="Enter new phone number if changing"
              maxLength={10}
            />
            {errors.newPhonenumber && <p className="text-red-500 text-xs mt-1">{errors.newPhonenumber}</p>}
          </div>
        )}
        
        {/* Gender */}
        <div>
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="gender">
            Gender *
          </label>
          <select
            id="gender"
            name="gender"
            value={formData.gender}
            onChange={handleChange}
            className={`shadow appearance-none border ${errors.gender ? 'border-red-500' : 'border-gray-300'} rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500`}
          >
            <option value="">Select Gender</option>
            <option value="Male">Male</option>
            <option value="Female">Female</option>
          </select>
          {errors.gender && <p className="text-red-500 text-xs mt-1">{errors.gender}</p>}
        </div>
        
        {/* Date of Birth */}
        <div>
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="dob">
            Date of Birth *
          </label>
          <input
            id="dob"
            name="dob"
            type="date"
            value={formData.dob}
            onChange={handleChange}
            className={`shadow appearance-none border ${errors.dob ? 'border-red-500' : 'border-gray-300'} rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500`}
          />
          {errors.dob && <p className="text-red-500 text-xs mt-1">{errors.dob}</p>}
        </div>
        
        {/* State */}
        <div>
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="state">
            State *
          </label>
          <select
            id="state"
            name="state"
            value={formData.state}
            onChange={handleChange}
            className={`shadow appearance-none border ${errors.state ? 'border-red-500' : 'border-gray-300'} rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500`}
          >
            <option value="">Select State</option>
            {states.map(state => (
              <option key={state} value={state}>{state}</option>
            ))}
          </select>
          {errors.state && <p className="text-red-500 text-xs mt-1">{errors.state}</p>}
        </div>
        
        {/* City */}
        <div>
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="city">
            City
          </label>
          <input
            id="city"
            name="city"
            type="text"
            value={formData.city}
            onChange={handleChange}
            className="shadow appearance-none border border-gray-300 rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Enter city"
          />
        </div>
        
        {/* Class Entry */}
        <div>
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="c_entry">
            Class Entry *
          </label>
          <select
            id="c_entry"
            name="c_entry"
            value={formData.c_entry}
            onChange={handleChange}
            className={`shadow appearance-none border ${errors.c_entry ? 'border-red-500' : 'border-gray-300'} rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500`}
          >
            <option value="">Select Class</option>
            <option value="Class 6th">Class 6th</option>
            <option value="Class 9th">Class 9th</option>
          </select>
          {errors.c_entry && <p className="text-red-500 text-xs mt-1">{errors.c_entry}</p>}
        </div>
        
        {/* School Choice */}
        <div>
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="c_school">
            School Choice *
          </label>
          <select
            id="c_school"
            name="c_school"
            value={formData.c_school}
            onChange={handleChange}
            className={`shadow appearance-none border ${errors.c_school ? 'border-red-500' : 'border-gray-300'} rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500`}
          >
            <option value="">Select School</option>
            {schools.map(school => (
              <option key={school} value={school}>{school}</option>
            ))}
          </select>
          {errors.c_school && <p className="text-red-500 text-xs mt-1">{errors.c_school}</p>}
        </div>
        
        {/* Password */}
        <div>
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="password">
            {isEditing ? 'New Password (leave blank to keep current)' : 'Password *'}
          </label>
          <input
            id="password"
            name="password"
            type="password"
            value={formData.password}
            onChange={handleChange}
            className={`shadow appearance-none border ${errors.password ? 'border-red-500' : 'border-gray-300'} rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500`}
            placeholder={isEditing ? "Enter new password" : "Enter password"}
          />
          {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password}</p>}
        </div>
      </div>
      
      {/* Submit Button */}
      <div className="mt-8 flex justify-end">
        <button
          type="button"
          onClick={() => onClose && onClose(false)}
          className="mr-2 px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center"
        >
          {isSubmitting ? (
            <>
              <FiLoader className="animate-spin mr-2" />
              {isEditing ? 'Updating...' : 'Registering...'}
            </>
          ) : (
            <>
              <FiSave className="mr-2" />
              {isEditing ? 'Update Student' : 'Register Student'}
            </>
          )}
        </button>
      </div>
    </form>
  );
};

export default Student;